import {
  Alert,
  AlertIcon,
  Divider,
  Flex,
  HStack,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ThemeContext, useAuth, usePracticeContext } from '@hb/react'
import {
  billingAuthDocUrlField,
  billingAuthNotesField,
  billingAuthOnFileField,
  billingAuthSignedOnField,
  birthingCenterModeField,
  colors,
  facilityTypeField,
  isActiveField,
  midwiferyNameField,
  midwivesCollection,
  notificationBillingAlertField,
  notificationCriticalNoticeField,
  notificationFirstNoticeField,
  pocusCertifiedField,
  pocusExpiresOnField,
  PracticeWithAdmin,
  specialtyField,
  WithId,
} from '@hb/shared'
import { useIsPracticeSuperAdmin } from '../../hooks/backend/auth/usePracticeRole'
import { useUpdateCollection } from '../../hooks/useAppCollections'
import { PracticeTemplateList } from '../Charting/DotPhraseTemplates/PracticeTemplateList'
import { CopyId } from '../CopyId'
import { Editable, EditableRow } from '../forms/Input'
import { DataLabel } from '../forms/Input/DataLabel'
import { PracticeAccessView } from './PracticeAccess/PracticeAccessView'
import { ProviderBillingInfo } from './ProviderBillingInfo'
import { ProviderContact } from './ProviderContact'
import { ProviderOtherInfo } from './ProviderOtherInfo'

const PracticeDotPhrasesPanel = ({ practiceId }: { practiceId: string }) => (
  <VStack w="100%" borderRadius={4} bg="white" overflow="hidden" align="stretch" spacing={0}>
    <Text
      px={3}
      py={2}
      fontSize="sm"
      fontWeight={600}
      bg="gray.50"
      borderBottomWidth="1px"
      borderColor="gray.200">
      Practice Dot Phrase Templates
    </Text>
    <PracticeTemplateList practiceId={practiceId} />
  </VStack>
)

const IsGroupField = ({ midwife }: { midwife: WithId<PracticeWithAdmin> | null }) => {
  const { updateItem } = useUpdateCollection(midwivesCollection)
  return (
    <HStack
      shadow="md"
      borderRadius="full"
      bg={colors.pink.hex}
      py="0.2rem"
      px={2}
      justify="space-between"
      w="100%">
      <RadioGroup
        colorScheme="green"
        onChange={v => updateItem(midwife?.id ?? '', 'isGroup', v === 'group')}
        value={midwife?.isGroup ? 'group' : 'solo'}>
        <HStack>
          <Radio bg="white" value="group">
            <Text color="gray.700" fontSize="sm" fontWeight={600}>
              Group
            </Text>
          </Radio>
          <Radio bg="white" value="solo">
            <Text whiteSpace="nowrap" color="gray.700" fontSize="sm" fontWeight={600}>
              Solo Provider
            </Text>
          </Radio>
        </HStack>
      </RadioGroup>
      <HStack ml="auto">
        <Editable
          style={{ flex: 1 }}
          field={isActiveField}
          value={!midwife?.isInactive}
          onSubmit={v => updateItem(midwife?.id ?? '', 'isInactive', !v)}
        />
      </HStack>
    </HStack>
  )
}

export const PracticeDataView = () => {
  const { updateItem } = useUpdateCollection(midwivesCollection)
  const role = useAuth(s => s.role)
  const { practice } = usePracticeContext()
  const isPracticeSuperAdmin = useIsPracticeSuperAdmin(practice?.id ?? '')
  return (
    <ThemeContext.Provider value={{ theme: 'detailed' }}>
      <VStack p={2} bg="gray.100" align="flex-start" spacing={2} w="100%">
        <VStack
          borderRadius={4}
          bg="white"
          w="100%"
          spacing={0}
          overflow="hidden"
          align="flex-start">
          <HStack w="100%" justify="space-between">
            <HStack bg={`${colors.pink.hex}aa`} spacing={0} flex={1} px={2}>
              <DataLabel fontSize="md">Practice</DataLabel>
              <Editable
                dataCellProps={{ fontSize: 'md' }}
                onSubmit={val => updateItem(practice?.id ?? '', 'name', val)}
                value={practice?.name}
                field={midwiferyNameField}
                style={{ flex: 1 }}
              />
            </HStack>
          </HStack>
          <Divider />
          <EditableRow
            label="Specialty"
            onSubmit={val => updateItem(practice?.id ?? '', 'specialty', val)}
            value={practice?.specialty}
            field={specialtyField}
          />
          <Divider />
          <EditableRow
            label="Facility Type"
            onSubmit={val => updateItem(practice?.id ?? '', 'facilityType', val)}
            value={practice?.facilityType}
            field={facilityTypeField}
          />
          <Divider />
          {/* §3.3: Birthing center operational mode — Mode 1 (complete record) or Mode 2 (facility EHR). */}
          <EditableRow
            label="Birthing Center Mode"
            onSubmit={val => updateItem(practice?.id ?? '', 'birthing_center_mode', val)}
            value={practice?.birthing_center_mode}
            field={birthingCenterModeField}
          />
          {practice?.birthing_center_mode === 'mode-2' && (
            <Alert status="info" borderRadius={0} fontSize="sm" px={3} py={2}>
              <AlertIcon />
              Mode 2 designates this system as the official facility medical record. NYS Article 28
              birthing center medical record requirements apply (§7.5). Confirm with your compliance
              officer before activating.
            </Alert>
          )}
          <Divider />
          <EditableRow
            label="First Notice (days)"
            onSubmit={val =>
              updateItem(practice?.id ?? '', 'notificationThresholds.firstNotice', val)
            }
            value={practice?.notificationThresholds?.firstNotice}
            field={notificationFirstNoticeField}
          />
          <Divider />
          <EditableRow
            label="Critical Notice (days)"
            onSubmit={val =>
              updateItem(practice?.id ?? '', 'notificationThresholds.criticalNotice', val)
            }
            value={practice?.notificationThresholds?.criticalNotice}
            field={notificationCriticalNoticeField}
          />
          <Divider />
          <EditableRow
            label="Billing Alert (days)"
            onSubmit={val =>
              updateItem(practice?.id ?? '', 'notificationThresholds.billingAlert', val)
            }
            value={practice?.notificationThresholds?.billingAlert}
            field={notificationBillingAlertField}
          />
          <Divider />
          <EditableRow
            label="POCUS Certified"
            onSubmit={val => updateItem(practice?.id ?? '', 'pocusCredentialing.certified', val)}
            value={practice?.pocusCredentialing?.certified}
            field={pocusCertifiedField}
          />
          <Divider />
          <EditableRow
            label="POCUS Expiry Date"
            onSubmit={val => updateItem(practice?.id ?? '', 'pocusCredentialing.expiresOn', val)}
            value={practice?.pocusCredentialing?.expiresOn}
            field={pocusExpiresOnField}
          />
          <Divider />
          <EditableRow
            label="Billing Auth on File"
            onSubmit={val =>
              updateItem(practice?.id ?? '', 'providerBillingAuthorization.onFile', val)
            }
            value={practice?.providerBillingAuthorization?.onFile}
            field={billingAuthOnFileField}
          />
          <Divider />
          <EditableRow
            label="Billing Auth Signed On"
            onSubmit={val =>
              updateItem(practice?.id ?? '', 'providerBillingAuthorization.signedOn', val)
            }
            value={practice?.providerBillingAuthorization?.signedOn}
            field={billingAuthSignedOnField}
          />
          <Divider />
          <EditableRow
            label="Billing Auth Document URL"
            onSubmit={val =>
              updateItem(practice?.id ?? '', 'providerBillingAuthorization.documentUrl', val)
            }
            value={practice?.providerBillingAuthorization?.documentUrl}
            field={billingAuthDocUrlField}
          />
          <Divider />
          <EditableRow
            label="Billing Auth Notes"
            onSubmit={val =>
              updateItem(practice?.id ?? '', 'providerBillingAuthorization.notes', val)
            }
            value={practice?.providerBillingAuthorization?.notes}
            field={billingAuthNotesField}
          />
          <Divider />
        </VStack>
        <IsGroupField midwife={practice} />
        <ProviderContact midwife={practice} />
        <ProviderBillingInfo midwife={practice} />
        <ProviderOtherInfo midwife={practice} />
        {role === 'super-admin' && practice ? (
          <PracticeAccessView practiceId={practice.id} />
        ) : null}
        {(role === 'super-admin' || isPracticeSuperAdmin) && practice ? (
          <PracticeDotPhrasesPanel practiceId={practice.id} />
        ) : null}
        <Flex w="100%" h="30px" position="relative">
          <CopyId item={{ id: practice?.id ?? '' }} />
        </Flex>
      </VStack>
    </ThemeContext.Provider>
  )
}
